import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Table, Button, Input, Select, Space, Tag, Tooltip, Popconfirm, message } from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  StopOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import type { ColumnType } from 'antd/es/table'
import { featureDefinitionsApi } from '@/api/feature-definitions'
import type { FeatureDefinition, StorageLocation } from '@/types/feature-definition'

const { Search } = Input

interface FeatureDefinitionListProps {
  onCreateClick: () => void
  onEditClick: (definition: FeatureDefinition) => void
  onRefresh?: () => void
}

export const FeatureDefinitionList = ({
  onCreateClick,
  onEditClick,
  onRefresh,
}: FeatureDefinitionListProps) => {
  const [search, setSearch] = useState('')
  const [storageFilter, setStorageFilter] = useState<StorageLocation | undefined>()
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>()
  const [deprecatedFilter, setDeprecatedFilter] = useState<boolean | undefined>()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  // Query for feature definitions
  const {
    data: definitionsData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [
      'feature-definitions',
      { search, storageFilter, categoryFilter, deprecatedFilter, page, pageSize },
    ],
    queryFn: () =>
      featureDefinitionsApi.list({
        search,
        storageLocation: storageFilter,
        categoryId: categoryFilter,
        deprecated: deprecatedFilter,
        page,
        pageSize,
      }),
  })

  // Query for categories
  const { data: categoriesData } = useQuery({
    queryKey: ['feature-categories'],
    queryFn: () => featureDefinitionsApi.listCategories(),
  })

  const handleDelete = async (id: string, key: string) => {
    try {
      await featureDefinitionsApi.delete(id)
      message.success(`Feature definition "${key}" deleted successfully`)
      refetch()
      onRefresh?.()
    } catch (error) {
      message.error('Failed to delete feature definition')
      console.error('Delete error:', error)
    }
  }

  const columns: ColumnType<FeatureDefinition>[] = [
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
      width: 200,
      fixed: 'left',
      render: (key: string, record: FeatureDefinition) => (
        <Space direction="vertical" size={0}>
          <strong>{key}</strong>
          {record.deprecated && (
            <Tag color="red" icon={<StopOutlined />}>
              Deprecated
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 250,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text: string | null) => text || <span style={{ color: '#999' }}>—</span>,
    },
    {
      title: 'Storage',
      dataIndex: 'storage_location',
      key: 'storage_location',
      width: 150,
      render: (location: StorageLocation) => {
        const colorMap: Record<StorageLocation, string> = {
          openfeature: 'blue',
          legacy_extra: 'orange',
          legacy_features: 'gold',
          attributes: 'purple',
        }
        return <Tag color={colorMap[location]}>{location.replace('_', ' ')}</Tag>
      },
    },
    {
      title: 'Data Type',
      dataIndex: 'data_type',
      key: 'data_type',
      width: 120,
      render: (type: string) => <Tag>{type}</Tag>,
    },
    {
      title: 'Availability',
      dataIndex: 'availability_rule',
      key: 'availability_rule',
      width: 150,
      render: (rule: string) => {
        const colorMap: Record<string, string> = {
          all: 'green',
          participant_only: 'blue',
          receiver_only: 'cyan',
          custom: 'purple',
        }
        return <Tag color={colorMap[rule] || 'default'}>{rule.replace('_', ' ')}</Tag>
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_: unknown, record: FeatureDefinition) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => onEditClick(record)}
              size="small"
            />
          </Tooltip>
          <Popconfirm
            title="Delete Feature Definition"
            description={`Are you sure you want to delete "${record.key}"? This will remove all assignments.`}
            onConfirm={() => handleDelete(record.id, record.key)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button type="link" danger icon={<DeleteOutlined />} size="small" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Filters */}
        <Space wrap>
          <Search
            placeholder="Search by key, name, or description"
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 300 }}
            prefix={<SearchOutlined />}
          />
          <Select
            placeholder="Storage Location"
            allowClear
            value={storageFilter}
            onChange={setStorageFilter}
            style={{ width: 180 }}
            options={[
              // Note: 'openfeature' intentionally excluded - managed by Feature Flags system
              { label: 'Legacy Extra', value: 'legacy_extra' },
              { label: 'Legacy Features', value: 'legacy_features' },
              { label: 'Attributes', value: 'attributes' },
            ]}
          />
          <Select
            placeholder="Category"
            allowClear
            value={categoryFilter}
            onChange={setCategoryFilter}
            style={{ width: 180 }}
            options={categoriesData?.data?.map((cat) => ({
              label: cat.display_name,
              value: cat.id,
            }))}
          />
          <Select
            placeholder="Status"
            allowClear
            value={deprecatedFilter}
            onChange={setDeprecatedFilter}
            style={{ width: 150 }}
            options={[
              { label: 'Active', value: false },
              { label: 'Deprecated', value: true },
            ]}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={onCreateClick}>
            New Feature Definition
          </Button>
        </Space>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={definitionsData?.data?.definitions || []}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 1200 }}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: definitionsData?.data?.total || 0,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} feature definitions`,
            onChange: (newPage, newPageSize) => {
              setPage(newPage)
              setPageSize(newPageSize)
            },
          }}
        />
      </Space>
    </div>
  )
}
